/**
 * AdminShell, wraps admin-mode routes with TopNav + AdminSubNav.
 *
 * Per D-087: admin mode is gated to role IN ('admin','owner'). Standard
 * users redirected to /home (they shouldn't reach an admin route URL
 * directly, but if they do, we send them back to user mode).
 *
 * TopNav renders with mode="admin" which swaps the gear icon for a
 * "Back to App" link → returns to user mode at /home.
 */

import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '../hooks/useSession.tsx';
import { TopNav } from './TopNav.tsx';
import { AdminSubNav } from './AdminSubNav.tsx';
import { BrandedLoader } from './BrandedLoader.tsx';
import { EmailVerificationBanner } from './EmailVerificationBanner.tsx';
import { OnboardingIncompleteBanner } from './OnboardingIncompleteBanner.tsx';
import { LowBalanceBanner } from './LowBalanceBanner.tsx';
// Round 14 Item B — full FooterChrome not mounted on authenticated shells.
// Push 72 mounts the small "Powered by Olytic" corner attribution instead
// so the platform nod survives a workspace rebrand (Salesforce Community
// pattern). Toggle-gated on brand_logo_r2_key so the vanilla Olytic-
// branded default doesn't render a redundant second attribution.
import { PoweredByOlytic } from './PoweredByOlytic.tsx';
import { scrollStorageKey } from '../hooks/useScrollToTop.tsx';

export function AdminShell() {
  const { status, user } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return <BrandedLoader size="lg" message="Loading admin…" />;
  }
  if (status === 'unauthenticated' || status === 'error' || user === null) {
    // Sub-agent HHHHH (2026-07-29) — protected-page session probe.
    // See UserShell.tsx for the full framing. Same envelope: any failure
    // on /api/me → redirect to /sign-in?expired=1 with a flash message,
    // no user click required.
    //
    // Sub-agent IIIII (2026-07-29) — Track 5: preserve `?next=` so admin
    // users land back on the exact /admin/* page they were on. SignIn's
    // safeNextPath allowlist already covers /admin/*, so this dovetails
    // cleanly.
    const currentPath = location.pathname + location.search;
    // Sub-agent LLLLL (2026-07-29) — anti-punt for Push 102 Track 5 scroll
    // preservation. Same shape as UserShell: stash scrollY so useScrollToTop
    // can restore it on the /admin/* mount that follows sign-in.
    if (typeof window !== 'undefined' && window.scrollY > 0) {
      try {
        sessionStorage.setItem(
          scrollStorageKey(currentPath),
          String(window.scrollY),
        );
      } catch {
        /* private browsing / disabled storage — no-op */
      }
    }
    const target = `/sign-in?expired=1&next=${encodeURIComponent(currentPath)}`;
    return <Navigate to={target} replace />;
  }

  // Role gate, only admin + owner can reach admin mode.
  if (user.role !== 'admin' && user.role !== 'owner') {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className="app-shell admin-mode">
      <TopNav mode="admin" />
      {/* Phase 1.21 (2026-06-29): persistent verify-email banner. Same
          mount semantics as UserShell. self-hides when verified, sits
          above the admin body so it shows on every /admin/* route. */}
      <EmailVerificationBanner />
      {/* Phase 1.32 (2026-06-29): safety-net banner for owners who landed
          on /admin/* without finishing setup. Same render rules as in
          UserShell — renders nothing for completed owners or non-owner
          roles. */}
      <OnboardingIncompleteBanner />
      {/* Push 4-1ah (2026-07-10): low-balance banner (account pool < 500
          credits for admin/owner). Dismissible per-session; self-hides at
          healthy balance. Threshold check + copy branches inside the
          component by role. */}
      <LowBalanceBanner />
      <div className="admin-body">
        <AdminSubNav role={user.role} />
        <main className="admin-main">
          <div className="admin-content">
            <Outlet />
          </div>
        </main>
      </div>
      {user.brand_logo_r2_key !== null && user.brand_logo_r2_key !== undefined && (
        <PoweredByOlytic variant="corner" />
      )}
    </div>
  );
}
